import type { ReactNode } from "react";
import * as stylex from "@stylexjs/stylex";
import { ButtonLink } from "@softmax/design-system";
import { steps } from "../onboarding-steps";
import type { OnboardingProgress } from "../onboarding-progress";
import { requestSentence, submitSentence } from "../onboarding-summary";
import { StalledHelp } from "../../stalled-help";
import { CommandCopy } from "../../command-copy";
import { styles } from "../onboarding.styles";

type OnboardingStepActionProps = Readonly<{
  step: (typeof steps)[number];
  progress: OnboardingProgress;
  authFailed: boolean;
  signInHref: string;
  onSignIn(): void;
  playerForm: ReactNode;
  agentHandOff: ReactNode;
  continueCommand: string;
  onCopy(command: string): Promise<void>;
  leagueName: string;
}>;

export function OnboardingStepAction({
  step,
  progress,
  authFailed,
  signInHref,
  onSignIn,
  playerForm,
  agentHandOff,
  continueCommand,
  onCopy,
  leagueName,
}: OnboardingStepActionProps) {
  switch (step.key) {
    case "signIn":
      return (
        <>
          {authFailed ? (
            <p {...stylex.props(styles.copy, styles.foreground)}>
              GitHub sent you back without a session. Try again.
            </p>
          ) : null}
          <div {...stylex.props(styles.action)}>
            <ButtonLink
              variant="primary"
              href={signInHref}
              onClick={(event) => {
                event.preventDefault();
                onSignIn();
              }}
            >
              {step.title}
            </ButtonLink>
          </div>
        </>
      );
    case "player":
      return playerForm;
    case "handOff":
      return agentHandOff;
    case "build":
      return (
        <>
          <p {...stylex.props(styles.copy, styles.small)}>
            Keep an eye on your agent’s window. If it asks a question or prints
            a line starting HANDOFF:, answer or do what it says there, in the
            same window; this page updates by itself.
          </p>
          <StalledHelp afterMs={120000} summary="Agent stopped?">
            <p {...stylex.props(styles.copy, styles.small)}>
              Paste this into the same window (closed it? start it again in the
              same folder first):
            </p>
            <CommandCopy command={continueCommand} onCopy={onCopy} />
            <p {...stylex.props(styles.copy, styles.small)}>
              Can’t upload? It needs you signed in: run{" "}
              <span {...stylex.props(styles.machine)}>
                uv run softmax login
              </span>{" "}
              in that folder.
            </p>
          </StalledHelp>
        </>
      );
    case "request":
      return (
        <CommandCopy
          command={requestSentence({ progress, leagueName })}
          onCopy={onCopy}
        />
      );
    case "submit":
      return (
        <CommandCopy
          command={submitSentence({ progress, leagueName })}
          onCopy={onCopy}
        />
      );
  }
}
